import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { trimToCents } from '../utils/format';

/**
 * SettlementLetterModal
 *
 * The letter the client signs: the distribution of their settlement.
 *
 * Pre-fills from /settlement-context — client details from Filevine, the
 * case financials from the overlay, and candidate lien lines gathered from
 * accepted negotiations, the Meds tab and the Liens tab. Each line shows
 * where its figure came from, because those three do not always agree.
 *
 * Totals are computed here and again in the generator. Nothing about the
 * arithmetic is typed: the sheet IS the sum, so a line edited is a net
 * recomputed.
 *
 * Only the Pre-Lit template is built. The rest are specified in
 * SETTLEMENT_TEMPLATES.md.
 */
function SettlementLetterModal({ projectId, clientName, close }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    client_name: clientName || '',
    client_last_name: (clientName || '').split(' ').slice(-1)[0] || '',
    // No default. Nothing the firm holds records a client's title, so a
    // pre-filled one is a guess from a first name — which is how a letter
    // to Christopher Sanchez went out to Ms. Sanchez. Blank addresses them
    // by their own full name.
    client_title: '',
    client_address: '',
    client_cell: '',
    client_email: '',
    matter_description: 'Motor Vehicle Accident',
    date_of_loss: '',
    settlement_amount: '',
    attorney_fees: '',
    costs: '',
    released_parties: '',
    delivery_method: 'SIGNED VIA VINESIGN & SENT VIA FEDEX',
  });
  const [liens, setLiens] = useState([]);
  // Three optional breakdowns, each a list of {name, amount}.
  //
  // Sources itemise where the settlement came from and must add up to it.
  // Splits divide the attorney fee with co-counsel or a firm we took the
  // case over from, and leave the client's net alone. Advances are loans,
  // client advances and child support, and are the only one of the three
  // that changes what the client actually receives.
  const [sources, setSources] = useState([]);
  const [splits, setSplits] = useState([]);
  const [advances, setAdvances] = useState([]);
  // Who this packet is being sent to. Defaulted from the case's track — a
  // pre-lit closeout is Cameron's, a litigation one is Garrett's — and
  // changed here when that person is out, which is the whole reason it is a
  // choice rather than a rule. It decides who Slack tags, not who may act:
  // any approver can still pick up any packet.
  const [approver, setApprover] = useState('');
  // Which letter this is. Opened from the case's Filevine phase rather than
  // asked, because Filevine already knows — but editable, because a case can
  // settle on one track and be closed out on the other.
  const [template, setTemplate] = useState('pre_lit');
  // Litigation only, and both off the signed release. Filevine does not hold
  // either one, so there is nothing to pre-fill them from.
  const [caption, setCaption] = useState('');
  const [courtCase, setCourtCase] = useState('');
  // The lawyer who reads a litigation closeout before Garrett does.
  const [attorney, setAttorney] = useState('');
  // The signed release. {release_id, filename, …what the reader found}. The
  // reading is a proposal: nothing it returns reaches a field until the
  // preparer puts it there.
  const [release, setRelease] = useState(null);
  const [reading, setReading] = useState(false);
  const [draftInfo, setDraftInfo] = useState(null);   // {saved_at, saved_by}
  // A packet already out for approval on this case. Resubmitting would
  // supersede it, and since an approver can now edit a submitted sheet, that
  // would silently throw away management's work. One live packet per case
  // used to mean "the newest wins"; it now means "wait, or withdraw it".
  const [pending, setPending] = useState(null);
  // The most recent packet an approver sent back on this case, if any.
  const [sentBack, setSentBack] = useState(null);
  // An approved packet on this case. The clean letter only exists once one
  // of these does, and until now it lived only in the Closeout tab — so a
  // preparer working on the case opened the Letters tab and got the
  // DRAFT-stamped copy with no way through to the real one.
  const [approved, setApproved] = useState(null);
  // Confirmation that the preparer has read what the approver altered.
  // Required before the final letter is handed over, and only when the
  // approver actually changed something.
  const [changesRead, setChangesRead] = useState(false);
  // Set once the preparer has accepted a signed-off packet and taken the
  // letter, so the form stops offering it a second time.
  const [accepted, setAccepted] = useState(false);
  // Set when the server refuses a sheet over must-fixes an approver could
  // clear but the preparer cannot. Their way forward is to send it up rather
  // than to be stuck.
  const [escalatable, setEscalatable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  // Confirmation that the released parties were checked against the signed
  // release. Held in component state only and never saved into the draft:
  // restoring yesterday's draft must not restore yesterday's confirmation.
  const [partiesConfirmed, setPartiesConfirmed] = useState(false);

  // Pre-send reconciliation against Filevine. `recon` holds the last result;
  // `acks` are the warning codes the negotiator has explicitly confirmed.
  const [recon, setRecon] = useState(null);
  const [checkedAt, setCheckedAt] = useState(null);
  const [checking, setChecking] = useState(false);
  const [acks, setAcks] = useState([]);
  const [ackNote, setAckNote] = useState('');
  const [sent, setSent] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/projects/${projectId}/settlement-context`,
          { withCredentials: true }
        );
        if (!alive) return;
        const d = r.data;
        setCtx(d);
        setForm((f) => ({
          ...f,
          client_name: d.client_name || f.client_name,
          client_last_name: (d.client_name || f.client_name || '').split(' ').slice(-1)[0] || '',
          client_address: (d.client_address_lines || []).join('\n'),
          client_cell: d.client_cell || '',
          client_email: d.client_email || '',
          date_of_loss: d.date_of_loss || '',
          settlement_amount: d.settlement_amount ?? '',
          attorney_fees: trimToCents(d.attorney_fees),
          costs: d.costs ?? '',
          released_parties: (d.carriers || []).join(', '),
        }));
        setApprover((a) => a || d.default_approver || '');
        if (d.track === 'lit') setTemplate((t) => (t === 'pre_lit' ? 'lit' : t));
        setAttorney((a) => a || (d.attorneys || [])[0]?.email || '');
        setLiens((d.lien_lines || []).map((l) => ({ ...l })));

        // The sheet comes back from whichever record of it is newest: the
        // last packet submitted on this case, or a draft saved since.
        //
        // Both used to be lost. Submitting deleted the draft outright, and
        // only a packet sent back for changes was ever read back — so a
        // preparer reopening a case that was approved, or still sitting
        // pending, got a form rebuilt from Filevine with every merged lien
        // line, corrected figure, fee split and loan they had typed gone.
        let packet = null;
        try {
          const q = await axios.get(
            `${API_URL}/api/settlement-approvals?project_id=${projectId}`
            + '&state=pending,changes_requested,approved',
            { withCredentials: true }
          );
          if (!alive) return;
          const rows = q.data.approvals || [];
          setPending(rows.find((a) => a.state === 'pending') || null);
          // Sent back for changes. Told to the preparer HERE, on the case
          // they are working on, and not only in Slack — a notification
          // nobody opens is not a handoff, and the packet is otherwise
          // invisible until somebody goes looking for it in the queue.
          const back = rows.find((a) => a.state === 'changes_requested') || null;
          setSentBack(back);
          setApproved(rows.find((a) => a.state === 'approved') || null);
          // A sent-back packet wins: that is the sheet somebody is being
          // asked to change. Otherwise the most recent one — the endpoint
          // already sorts newest first.
          packet = back || rows[0] || null;
        } catch (_) { /* the queue is not worth failing the form over */ }

        const applyPacket = () => {
          const pl = packet && packet.payload;
          if (!pl) return;
          setForm((f) => ({ ...f, ...formFromPayload(pl) }));
          setLiens(liensFromPayload(pl));
          setSources(rowsFromPayload(pl.settlement_sources));
          setSplits(rowsFromPayload(pl.fee_splits));
          setAdvances(rowsFromPayload(pl.advances));
          setDraftInfo({ from: 'packet', state: packet.state,
                         saved_at: packet.created_at,
                         saved_by: packet.prepared_by_name || packet.prepared_by });
        };

        const applyDraft = () => {
          const { form: savedForm, liens: savedLiens, saved_at, saved_by } = d.draft;
          // A blank in the draft never beats a value the case now has.
          //
          // The draft stores the whole form, blanks included. Spreading it
          // wholesale meant that a draft saved before the address or date of
          // loss had been read back from Filevine pinned those boxes empty
          // for good — the pre-fill ran, and was then overwritten by the
          // empty string sitting in the draft. The preparer saw two blank
          // fields on a case that plainly had the data and had no way to tell
          // the difference between "not found" and "cleared by the draft".
          //
          // A field the preparer deliberately emptied will therefore come
          // back on reload. That is the right way round: a settlement letter
          // with no client address is never what anybody meant, and a
          // reappearing value is visible where a pinned blank is not.
          if (savedForm) {
            setForm((f) => {
              const merged = { ...f };
              Object.entries(savedForm).forEach(([k, v]) => {
                if (v === '' || v === null || v === undefined) return;
                merged[k] = k === 'attorney_fees' ? trimToCents(v) : v;
              });
              return merged;
            });
          }
          if (Array.isArray(savedLiens)) setLiens(savedLiens.map((l) => ({ ...l })));
          // The breakdowns are lists, not fields: an empty one is a real
          // state — every loan paid off, the fee no longer shared — so these
          // are restored as saved rather than merged.
          if (d.draft.release) setRelease(d.draft.release);
          if (d.draft.template) setTemplate(d.draft.template);
          if (typeof d.draft.caption === 'string') setCaption(d.draft.caption);
          if (typeof d.draft.court_case === 'string') setCourtCase(d.draft.court_case);
          if (typeof d.draft.attorney === 'string') setAttorney(d.draft.attorney);
          if (Array.isArray(d.draft.sources)) setSources(rowsFromPayload(d.draft.sources));
          if (Array.isArray(d.draft.splits)) setSplits(rowsFromPayload(d.draft.splits));
          if (Array.isArray(d.draft.advances)) setAdvances(rowsFromPayload(d.draft.advances));
          // The note is typed work and comes back. The tick-boxes do not:
          // they are confirmations of findings that are recomputed on the
          // next check, and restoring them would pre-confirm facts nobody
          // has re-read.
          if (typeof d.draft.ack_note === 'string') setAckNote(d.draft.ack_note);
          setDraftInfo({ from: 'draft', saved_at, saved_by });
        };

        // Oldest first, so the newest record of the sheet is the one left
        // standing. A draft saved before the packet was submitted is the
        // older of the two and must not overwrite what went to closeout.
        //
        // The packet's time is the LAST thing that happened to it, not
        // created_at. created_at is when the preparer first submitted it,
        // which is always before any edit an approver made to it — so a
        // draft saved between submitting and the approver's edit looked
        // newer than the edit and silently replaced it. The preparer read
        // the note saying their figures had been changed, looked at the
        // sheet, and saw their own numbers unchanged. Reported 2026-11-02.
        const at = (v) => (v ? new Date(v).getTime() || 0 : 0);
        const packetAt = packet
          ? Math.max(at(packet.revised_at), at(packet.decided_at),
                     at(packet.created_at))
          : 0;
        [
          packet && packet.payload && [packetAt, applyPacket],
          d.draft && [at(d.draft.saved_at), applyDraft],
        ].filter(Boolean)
         .sort((a, b) => a[0] - b[0])
         .forEach(([, run]) => run());
      } catch (e) {
        if (alive) setError('Could not load case data: ' + (e.response?.data?.detail || e.message));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId, clientName]);

  const set = (k) => (e) => {
    // Changing the released parties invalidates any prior confirmation —
    // otherwise confirm-then-edit-then-send would slip through.
    if (k === 'released_parties') setPartiesConfirmed(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };
  const n = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v) || 0);
  const money = (v) =>
    `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const gross = n(form.settlement_amount);
  const fees = n(form.attorney_fees);
  const costs = n(form.costs);
  const liensTotal = liens.reduce((a, l) => a + n(l.amount), 0);
  const sourcesTotal = sources.reduce((a, r) => a + n(r.amount), 0);
  const splitsTotal = splits.reduce((a, r) => a + n(r.amount), 0);
  const advancesTotal = advances.reduce((a, r) => a + n(r.amount), 0);
  // What is left for the client before anything comes out of their own
  // share, and what they actually receive after it does. With no advances
  // the two are the same figure and only the net is shown, here and on the
  // letter.
  const grossProceeds = gross - fees - costs - liensTotal;
  const net = grossProceeds - advancesTotal;
  // This firm's share of the fee is the remainder, never typed. Two figures
  // that must add up to a third are one slip away from a sheet that does
  // not, and the total is what the client signs.
  const firmShare = fees - splitsTotal;

  // Rule B8: the retainer rate. The fee is editable so a specialist can
  // reduce it to improve the client's net; the label then says so, which is
  // what the firm's own letters show.
  const isLit = template === 'lit';
  const retainerPct = ctx?.attorney_fees_pct ?? 100 / 3;
  const feePct = gross ? (fees / gross) * 100 : 0;
  const reduced = gross > 0 && feePct < retainerPct - 0.05;
  const feeLabel = !gross
    ? '—'
    : reduced
      ? `${feePct.toFixed(2)}%, reduced from ${retainerPct.toFixed(retainerPct % 1 ? 1 : 0)}%`
      : `${feePct.toFixed(feePct % 1 ? 1 : 0)}%`;

  // What the approver altered, looked up by the figure it belongs to.
  //
  // The list at the top of the form says two figures moved. On a sheet with
  // fifteen boxes on it that does not say WHICH box, so every changed figure
  // is also marked where it sits, with the old value struck through beside
  // it — the same red strike the approver sees on the net they moved.
  //
  // Row changes carry the row's own name from the server. The matching that
  // decided a row HAD changed is done there, against its own normalisation;
  // re-deriving that here is how the two would drift apart.
  const fromApprover = (sentBack || approved || {}).changes || [];
  const changedField = {};
  const changedRow = {};
  fromApprover.forEach((c) => {
    if (c.name) changedRow[String(c.name).trim().toLowerCase()] = c;
    else if (c.field) changedField[c.field] = c;
  });
  const rowChange = (name) => changedRow[String(name || '').trim().toLowerCase()];

  // The server sends {name, where}; older responses sent bare strings. One
  // normaliser rather than a conditional at every use.
  const skipped = (ctx?.skipped_no_negotiation || []).map((row) =>
    (typeof row === 'string' ? { name: row, where: 'meds' } : row));

  const addressLines = (form.client_address || '').split('\n').filter(Boolean);
  const missing = [
    !form.client_name && 'client name',
    addressLines.length === 0 && 'address',
    !gross && 'settlement',
    !form.released_parties.trim() && 'released parties',
    // The generator refuses a litigation letter with no caption; asked for
    // here so it reads as a missing field rather than a 422 after pressing a
    // button. A reviewing attorney is required for the same reason — a lit
    // packet has nowhere to go without one.
    isLit && !caption.trim() && 'case caption',
    isLit && !attorney && 'reviewing attorney',
  ].filter(Boolean);
  // The same two rules the generator enforces, checked here so the preparer
  // reads them beside the figures rather than as a 422 after pressing a
  // button. The generator keeps its copy: this one is a courtesy, that one
  // is the guarantee.
  const sheetErrors = [
    sources.length > 0 && Math.abs(sourcesTotal - gross) > 0.005
      && `The settlement sources add up to ${money(sourcesTotal)}, but the `
         + `settlement is ${money(gross)}. They are the same money broken out.`,
    splitsTotal > fees + 0.005
      && `The fee split adds up to ${money(splitsTotal)}, more than the `
         + `${money(fees)} in attorney fees. A division comes out of the fee.`,
  ].filter(Boolean);
  const canGenerate = missing.length === 0 && net >= 0 && sheetErrors.length === 0
    && partiesConfirmed && !busy;
  // The approved letter is released once the preparer has read what the
  // approver changed. An approval that changed nothing has nothing to read.
  // A packet the approver signed off on as they left it. The preparer's
  // acceptance releases the letter; a packet merely sent back does not.
  const signedOff = !!sentBack && !!sentBack.cleared_on_accept && !sent && !accepted;
  const acceptReady = signedOff && changesRead && !busy;

  const finalReady = !!approved && !busy
    && ((approved.changes || []).length === 0 || changesRead);
  const unackedCount = recon
    ? (recon.warnings || []).filter((f) => !acks.includes(f.code)).length : 0;
  const canSubmit = canGenerate && recon
    && (recon.blocking || []).length === 0 && unackedCount === 0;

  const saveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.put(
        `${API_URL}/api/projects/${projectId}/settlement-draft`,
        { draft: { form, liens, sources, splits, advances, ack_note: ackNote,
                   template, caption, court_case: courtCase, attorney, release } },
        { withCredentials: true }
      );
      setSavedAt(new Date());
      setDraftInfo(null);
    } catch (e) {
      setError('Could not save: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  // Fold one lien line into the one above it, summing the amounts. The same
  // lienholder sometimes arrives under two names — DHCS and the health plan
  // that administers it — and the letter should show one line, not two.
  const mergeUp = (i) => {
    if (i < 1) return;
    setLiens((xs) => xs.map((x, j) => {
      if (j !== i - 1) return x;
      const other = xs[i];
      // The originals add up only when both lines mean the same thing by
      // them. A provider billed and a health insurer asserted; summing those
      // into one bracket on the letter would describe neither. When they
      // disagree the merged line carries nothing, which drops its bracket and
      // suppresses the totalling sentence — correct, and visible.
      const sameKind = (x.original_kind || 'billed') === (other.original_kind || 'billed');
      const bothKnown = x.original != null && x.original !== ''
        && other.original != null && other.original !== '';
      return {
        ...x,
        amount: (Number(x.amount) || 0) + (Number(other.amount) || 0),
        original: sameKind && bothKnown
          ? (Number(x.original) || 0) + (Number(other.original) || 0)
          : '',
        source: 'merged',
      };
    }).filter((_, j) => j !== i));
  };

  // One payload shape, used by the draft generator, the checker and the
  // closeout submission, so the three can never drift apart.
  const payload = () => ({
    template,
    // Only meaningful on a litigation letter; the builder ignores them on
    // pre-lit, which keeps a case that moved tracks from printing a caption
    // on a pre-lit block.
    case_caption: isLit ? (caption.trim() || null) : null,
    court_and_case_no: isLit ? (courtCase.trim() || null) : null,
    attorney_email: isLit ? (attorney || null) : null,
    release_id: release?.release_id || null,
    release_filename: release?.filename || null,
    client_name: form.client_name,
    client_address_lines: addressLines,
    client_last_name: form.client_last_name || null,
    client_title: form.client_title || null,
    client_cell: form.client_cell || null,
    client_email: form.client_email || null,
    matter_description: form.matter_description,
    date_of_loss: form.date_of_loss || null,
    settlement_amount: gross,
    fee_label: feeLabel,
    attorney_fees: fees,
    costs,
    // Four elements: what we pay, and what the lienholder originally billed
    // or asserted, which prints in brackets on the letter. An empty original
    // is sent as null rather than 0 — nothing on file is not a claim of zero.
    liens: liens.filter((l) => l.name && n(l.amount) > 0).map((l) => [
      l.name, n(l.amount),
      l.original === '' || l.original == null ? null : n(l.original),
      l.original_kind || 'billed',
    ]),
    // Named exactly as the generator's parameters: the server passes the
    // payload straight through, so a name that drifts becomes an unexpected
    // keyword argument at generate time rather than a visible mistake here.
    settlement_sources: rowsOut(sources, n),
    fee_splits: rowsOut(splits, n),
    advances: rowsOut(advances, n),
    released_parties: form.released_parties,
    delivery_method: form.delivery_method,
    approver_email: approver || null,
  });

  const errText = async (e) => {
    let msg = e.message;
    if (e.response?.data instanceof Blob) {
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* keep */ }
    } else if (e.response?.data?.detail) {
      const d = e.response.data.detail;
      msg = typeof d === 'string' ? d : (d.message || msg);
    }
    return msg;
  };

  // Check the sheet against Filevine without submitting anything.
  const runChecks = async () => {
    setChecking(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/projects/${projectId}/settlement-reconciliation`,
        payload(), { withCredentials: true }
      );
      // Re-checking keeps a tick only where the finding came back word for
      // word. The same code with a different message is a different fact —
      // the figure moved — and has to be read again rather than inherit a
      // confirmation given about the old one.
      const before = new Map((recon?.warnings || []).map((f) => [f.code, f.message]));
      const after = r.data.warnings || [];
      setAcks((xs) => xs.filter((c) => {
        const found = after.find((f) => f.code === c);
        return found && before.get(c) === found.message;
      }));
      setRecon(r.data);
      setEscalatable(false);
      setCheckedAt(new Date());
    } catch (e) {
      setError(await errText(e));
    } finally {
      setChecking(false);
    }
  };

  /**
   * The approved, unstamped letter — from the case the preparer is working
   * on, rather than only from the Closeout queue.
   *
   * The server releases this on approved state alone, not on who is asking,
   * so nothing about the approval gate changes here: before approval the
   * endpoint refuses it and the draft is all there is.
   */
  const downloadFinal = async () => {
    if (!approved) return;
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/settlement-approvals/${approved.id}/document?final=true`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Settlement Letter_${form.client_name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(await errText(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Accept the changes the approver signed off on, and take the letter.
   *
   * Only offered on a packet they marked "Approve with my changes", which
   * is an approval of the sheet as they left it — so there is no second
   * round, and the letter released is the stored one built from their
   * figures. The server refuses this on a packet that was merely sent back,
   * so the button cannot create an approval that nobody gave.
   */
  const acceptAndFinish = async () => {
    if (!sentBack) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/settlement-approvals/${sentBack.id}/accept`,
        {}, { withCredentials: true }
      );
      const res = await axios.get(
        `${API_URL}/api/settlement-approvals/${sentBack.id}/document?final=true`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Settlement Letter_${form.client_name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setAccepted(true);
    } catch (e) {
      setError(await errText(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Take the approver's edits as they stand and send the sheet back up.
   *
   * The sheet on screen is already their version — it was restored from the
   * packet they sent back — so there is nothing to retype. What this saves
   * is the round of reading every line again to find the two they moved,
   * which the change list above has already named.
   *
   * It still goes through the reconciliation: accepting an approver's
   * figures is not the same as the case agreeing with them, and a lien
   * entered since could make the sheet wrong in a way neither of them has
   * seen. Clean, it sends; anything to confirm, it stops and shows it, and
   * the ordinary Send button takes over from there.
   */
  // The attorney cleared it with changes. Reading them is the preparer's
  // job; sending it on is one button. Deliberately not the accept endpoint,
  // which releases a letter — this only moves the packet to closeout.
  const uploadRelease = async (fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    setReading(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const r = await axios.post(
        `${API_URL}/api/projects/${projectId}/settlement-release`,
        body, { withCredentials: true });
      setRelease(r.data);
    } catch (e) {
      setError(await errText(e));
    } finally {
      setReading(false);
    }
  };

  const sendOnToCloseout = async () => {
    if (!sentBack) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/settlement-approvals/${sentBack.id}/send-to-closeout`,
        {}, { withCredentials: true });
      setSent(true);
    } catch (e) {
      setError(await errText(e));
    } finally {
      setBusy(false);
    }
  };

  const acceptAndResubmit = async () => {
    setChecking(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/projects/${projectId}/settlement-reconciliation`,
        payload(), { withCredentials: true }
      );
      setRecon(r.data);
      setCheckedAt(new Date());
      setEscalatable(false);
      const clean = (r.data.blocking || []).length === 0
        && (r.data.warnings || []).length === 0;
      if (clean) await submitForApproval(false);
    } catch (e) {
      setError(await errText(e));
    } finally {
      setChecking(false);
    }
  };

  const sheetSig = JSON.stringify([gross, fees, costs, form.released_parties,
    liens.map((l) => [l.name, n(l.amount), l.original, l.original_kind])]);
  useEffect(() => {
    setRecon(null);
    setCheckedAt(null);
    setAcks([]);
  }, [sheetSig]);

  const toggleAck = (code) =>
    setAcks((xs) => (xs.includes(code) ? xs.filter((c) => c !== code) : [...xs, code]));

  const submitForApproval = async (escalate = false) => {
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/projects/${projectId}/settlement-approval`,
        { ...payload(), acknowledged: acks, acknowledgement_note: ackNote || null,
          escalate },
        { withCredentials: true }
      );
      setSent(r.data);
      setRecon(null);
      // The worksheet is NOT cleared here. It used to be: submitting deleted
      // the draft on the reasoning that a generated letter meant the form was
      // finished with. It is not finished with — the packet can be sent back,
      // the figures can move, and the sheet is the only record of what the
      // preparer typed. Reopening now restores the submitted packet.
    } catch (e) {
      // A 409 carries the findings that stopped it, so show them rather than
      // a bare message the negotiator cannot act on.
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d && typeof d === 'object' && d.reconciliation) {
        setRecon(d.reconciliation);
        setEscalatable(!!d.escalatable);
        setError(d.message);
      } else {
        setError(await errText(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/projects/${projectId}/letters/settlement`,
        payload(),
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Settlement Letter DRAFT_${form.client_name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // The draft is not the end of the job \u2014 the sheet still has to go to
      // closeout \u2014 so the saved draft stays put.
    } catch (e) {
      setError(await errText(e));
    } finally {
      setBusy(false);
    }
  };

  const srcLabel = { negotiation: 'agreed', meds: 'Meds tab', lien: 'Liens tab' };

  return (
    // Rendered inside the case overlay, which closes the case on any
    // click that reaches it. Without this every click in here — a
    // field, a label, anything — bubbled up and threw the preparer
    // back to the case list mid-form.
    <div style={s.backdrop} onClick={(e) => e.stopPropagation()}>
      <div style={s.modal}>
        <div style={s.title}>Settlement letter</div>
        <div style={s.subtitle}>
          The client signs this — every figure below prints on it.
        </div>

        {/* Which letter this is. Opened from the case's Filevine phase, and
            changeable, because a case can settle on one track and be closed
            out on the other. Litigation adds the caption block and the
            dismissal sentence, and sends the packet to a lawyer first. */}
        <div style={s.section}>Template</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[['pre_lit', 'Pre-Lit'], ['lit', 'Lit']].map(([key, label]) => (
            <button key={key} type="button"
                    style={template === key ? s.tplOn : s.tplOff}
                    onClick={() => setTemplate(key)}>
              {label}
            </button>
          ))}
          {ctx?.track && (
            <span style={{ ...s.hint, alignSelf: 'center' }}>
              Filevine has this case in {ctx.track === 'lit' ? 'litigation' : 'pre-litigation'}.
            </span>
          )}
        </div>

        {/* The release, above the fields it feeds. The sheet already refuses
            to send until somebody ticks "checked against the signed release";
            with the document here that tick is a check rather than a promise,
            and the attorney and the approver read the same one. */}
        <div style={s.section}>Signed release</div>
        {release ? (
          <div style={s.panel}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#171D29' }}>
                  {release.filename}
                </div>
                <div style={s.hint}>
                  {Math.round((release.size || 0) / 1024)} KB
                  {release.found?.length
                    ? ` · read ${release.found.length} of 4 fields`
                    : ' · nothing could be read from it'}
                </div>
              </div>
              <a style={s.cancel} href={`${API_URL}/api/settlement-releases/${release.release_id}`}
                 target="_blank" rel="noreferrer">Open</a>
              <label style={{ ...s.ghost, cursor: 'pointer' }}>
                Replace
                <input type="file" style={{ display: 'none' }}
                       accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff"
                       onChange={(e) => uploadRelease(e.target.files)} />
              </label>
            </div>

            {!release.has_text_layer && (
              <div style={s.warn}>
                This one is a scan, so nothing could be read out of it. It is
                stored and everyone can open it — the fields below are typed.
              </div>
            )}

            {/* Proposals, never fills. Each one is a button, because a value
                nobody chose is a value nobody checked. */}
            {(release.court_and_case_no || release.case_caption || release.date_of_loss) && (
              <div style={{ marginTop: 10 }}>
                <label style={s.label}>From the release</label>
                {release.case_caption && (
                  <div style={s.refline}>
                    {release.case_caption}
                    <button style={{ ...s.ghost, marginLeft: 8 }}
                            onClick={() => { setTemplate('lit'); setCaption(release.case_caption); }}>
                      use as caption
                    </button>
                  </div>
                )}
                {release.court_and_case_no && (
                  <div style={s.refline}>
                    {release.court_and_case_no}
                    <button style={{ ...s.ghost, marginLeft: 8 }}
                            onClick={() => { setTemplate('lit'); setCourtCase(release.court_and_case_no); }}>
                      use as court
                    </button>
                  </div>
                )}
                {release.date_of_loss && (
                  <div style={s.refline}>
                    {release.date_of_loss}
                    <button style={{ ...s.ghost, marginLeft: 8 }}
                            onClick={() => setForm((f) => ({ ...f, date_of_loss: release.date_of_loss }))}>
                      use as date of loss
                    </button>
                  </div>
                )}
              </div>
            )}

            {release.released_parties_passage && (
              <div style={{ marginTop: 10 }}>
                <label style={s.label}>Who it releases — read this, it is not a list</label>
                <div style={{ ...s.refline, fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>
                  …{release.released_parties_passage}…
                </div>
                <div style={s.hint}>
                  Defined terms and open-ended categories, different in every
                  release. Nothing is copied into the released-parties field
                  from this — a short confident list is how a party gets left
                  off a letter.
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={s.panel}>
            <label style={{ ...s.ghost, cursor: 'pointer', display: 'inline-block' }}>
              {reading ? 'Reading…' : '+ upload the signed release'}
              <input type="file" style={{ display: 'none' }}
                     accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff"
                     onChange={(e) => uploadRelease(e.target.files)} />
            </label>
            <div style={s.hint}>
              PDF or a scan. It travels with the packet, so the attorney and
              the approver check against the same document you did.
            </div>
          </div>
        )}

        {isLit && (
          <>
            <div style={s.section}>Case caption</div>
            <F label="Caption" v={caption} on={(e) => setCaption(e.target.value)} req
               placeholder="Campbell v. Estate of Joseph Ashton Gonzalez, et al." />
            <F label="Court and case number" v={courtCase}
               on={(e) => setCourtCase(e.target.value)}
               placeholder="Merced County Superior Court, Case No. 24CV-06557" />
            <div style={s.warn}>
              Both come off the <b>signed release</b>, not Filevine — it does not
              hold them. The caption prints on a letter the client signs, so it
              has to read the way the court writes it.
            </div>
          </>
        )}

        {loading && <div style={s.loading}>Loading case data…</div>}
        {draftInfo && (
          <div style={s.warn}>
            {draftInfo.from === 'packet'
              ? `Restored the sheet sent to closeout${draftInfo.state === 'approved' ? ' and approved' : ''}`
              : 'Restored a saved draft'}
            {draftInfo.saved_by ? ` by ${draftInfo.saved_by}` : ''}
            {draftInfo.saved_at ? `, ${new Date(draftInfo.saved_at).toLocaleString()}` : ''}.
            Figures below are what was saved, not freshly pulled from the case.
          </div>
        )}
        {approved && !sent && !sentBack && (
          <div style={s.okbox}>
            <strong>
              Approved by {approved.decided_by_name || approved.decided_by}
              {approved.decided_at
                ? ` on ${new Date(approved.decided_at).toLocaleDateString()}`
                : ''}.
            </strong>
            {approved.decision_note && (
              <div style={{ marginTop: 5 }}>“{approved.decision_note}”</div>
            )}
            {(approved.changes || []).length > 0 ? (
              <>
                <div style={{ marginTop: 6 }}>
                  <strong>They changed {approved.changes.length}{' '}
                  {approved.changes.length === 1 ? 'figure' : 'figures'} before
                  approving:</strong>
                </div>
                <ul style={s.ul}>
                  {approved.changes.map((c, i) => (
                    <li key={`${c.field}:${i}`}>
                      {c.label}: {c.was_text} → <strong>{c.now_text}</strong>
                    </li>
                  ))}
                </ul>
                {/* The approved letter carries their figures, not the ones
                    the preparer sent. Handing it over without their having
                    read what moved is how a changed number reaches a client
                    with nobody on this side having seen it. */}
                <label style={s.checkRow}>
                  <input type="checkbox" checked={changesRead}
                         onChange={(e) => setChangesRead(e.target.checked)} />
                  <span>I have read what the approver changed.</span>
                </label>
              </>
            ) : (
              <div style={{ marginTop: 5 }}>
                They approved it as you sent it — no figures were changed.
              </div>
            )}
            <div style={{ marginTop: 5 }}>
              The final letter below is the approved one, without the DRAFT
              stamp. Editing this sheet starts a new packet and it has to be
              approved again.
            </div>
          </div>
        )}
        {/* A litigation packet a lawyer has cleared with changes. It is not a
            send-back: nothing is wrong with it, and the preparer's one job is
            to read what the attorney changed and send it on. Their words, not
            a summary — Garrett will ask about the attorney's reasoning, and
            the preparer should not be the one inventing it. */}
        {sentBack && !sent && sentBack.attorney_handback && (
          <div style={s.warn}>
            <strong>
              {(sentBack.attorney_cleared || {}).by_name || 'The reviewing attorney'}
              {' '}cleared this and changed the sheet.
            </strong>
            {(sentBack.attorney_cleared || {}).note && (
              <div style={{ marginTop: 5 }}>“{sentBack.attorney_cleared.note}”</div>
            )}
            {((sentBack.attorney_cleared || {}).changes || []).length > 0 && (
              <ul style={s.ul}>
                {sentBack.attorney_cleared.changes.map((c, i) => (
                  <li key={`${c.field}:${i}`}>
                    {c.label}: {c.was_text} → <strong>{c.now_text}</strong>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ marginTop: 6 }}>
              Read it over. Change anything you need to and send it again, or
              send it straight on — their reason travels with it either way.
            </div>
            <div style={{ marginTop: 8 }}>
              <button style={s.primary} disabled={busy} onClick={sendOnToCloseout}>
                Send on to closeout
              </button>
            </div>
          </div>
        )}

        {sentBack && !sent && !sentBack.attorney_handback && (
          <div style={s.error}>
            <strong>
              {signedOff ? 'Signed off with changes by ' : 'This case was sent back by '}
              {sentBack.decided_by_name || sentBack.decided_by}
              {sentBack.decided_at
                ? ` on ${new Date(sentBack.decided_at).toLocaleDateString()}`
                : ''}.
            </strong>
            {sentBack.decision_note && (
              <div style={{ marginTop: 5 }}>“{sentBack.decision_note}”</div>
            )}
            {/* What they changed themselves, as against what they asked for.
                An approver can edit the sheet before sending it back, and the
                sheet below already carries those edits — so the preparer has
                to be shown them, or figures will have moved under them with
                nothing on screen saying so. */}
            {(sentBack.changes || []).length > 0 && (
              <>
                <div style={{ marginTop: 6 }}>
                  <strong>They changed {sentBack.changes.length}{' '}
                  {sentBack.changes.length === 1 ? 'figure' : 'figures'} themselves:</strong>
                </div>
                <ul style={s.ul}>
                  {sentBack.changes.map((c, i) => (
                    <li key={`${c.field}:${i}`}>
                      {c.label}: {c.was_text} → <strong>{c.now_text}</strong>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div style={{ marginTop: 5 }}>
              {signedOff
                ? 'They have approved the sheet as they left it. Read what they '
                  + 'changed, accept it, and the final letter is yours — no '
                  + 'second round. If you need something different, change it '
                  + 'here and send it to closeout again instead.'
                : (sentBack.changes || []).length > 0
                  ? 'The sheet below already has their changes in it. Make the rest and send it to closeout again — or, if their edits are the whole of it, accept them below.'
                  : 'The sheet below is the one you sent, exactly as it was. Make the changes and send it to closeout again.'}
            </div>
            {signedOff && (
              <label style={s.checkRow}>
                <input type="checkbox" checked={changesRead}
                       onChange={(e) => setChangesRead(e.target.checked)} />
                <span>I have read what the approver changed.</span>
              </label>
            )}
          </div>
        )}
        {pending && !sent && (
          <div style={s.warn}>
            <strong>This case is already out for approval</strong> — sent by{' '}
            {pending.prepared_by_name || pending.prepared_by}
            {pending.revision > 1
              ? `, and edited since by ${pending.revised_by_name || 'an approver'}`
              : ''}.
            Sending again would replace it and throw away anything an approver has
            changed. Withdraw it from Closeout first, or wait for a decision.
            {(pending.changes || []).length > 0 && (
              <ul style={s.ul}>
                {pending.changes.map((c, i) => (
                  <li key={`${c.field}:${i}`}>
                    {c.label}: {c.was_text} → <strong>{c.now_text}</strong>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {(ctx?.prefill_missing || []).length > 0 && (
          <div style={s.warn}>
            Filevine had nothing for {ctx.prefill_missing.join(', and ')}. The
            boxes below are empty because the case does not have it, not
            because the form failed to read it — type it in and it will print.
          </div>
        )}
        {error && <div style={s.error}>{error}</div>}

        <div style={s.section}>Client</div>
        <div style={s.row}>
          <F label="Name" v={form.client_name} on={set('client_name')} req />
          <F label="Surname" v={form.client_last_name} on={set('client_last_name')} />
          <F label="Title" v={form.client_title} on={set('client_title')}
             placeholder="optional — blank uses their full name" />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>Address <span style={{ color: '#dc2626' }}>*</span></label>
          <textarea style={{ ...s.textarea, minHeight: 50 }} value={form.client_address}
                    onChange={set('client_address')} placeholder={'123 Example St\nSanta Barbara, CA 93101'} />
        </div>
        <div style={s.row}>
          <F label="Cell" v={form.client_cell} on={set('client_cell')} />
          <F label="Email" v={form.client_email} on={set('client_email')} />
        </div>
        <div style={s.row}>
          <F label="RE line" v={form.matter_description} on={set('matter_description')} />
          <F label="Date of loss" v={form.date_of_loss} on={set('date_of_loss')} placeholder="MM/DD/YYYY" />
        </div>

        <div style={s.section}>Distribution</div>
        <table style={s.table}>
          <tbody>
            <tr>
              <td><b>TOTAL SETTLEMENT</b></td>
              <td style={s.r}><input style={s.num} value={form.settlement_amount} onChange={set('settlement_amount')} /><Was change={changedField.settlement_amount} /></td>
            </tr>
            {/* Where the settlement came from, when it came from more than
                one place. Not extra money — the same money itemised — so
                these have to add up to the line above. */}
            <BreakRows rows={sources} setRows={setSources}
                       placeholder="Third party / First party / Medical payments"
                       addLabel="+ break down the settlement by source"
                       aria="Settlement source" changeFor={rowChange} />
            <tr>
              <td>ATTORNEY FEES <span style={s.tag}>{feeLabel}</span></td>
              <td style={s.r}><input style={s.num} value={form.attorney_fees} onChange={set('attorney_fees')} /><Was change={changedField.attorney_fees} /></td>
            </tr>
            {/* A fee shared with co-counsel, or with the firm a case was
                taken over from. The client's net does not move: this is a
                disclosure of who is paid out of the fee above, which is what
                Rule 1.5.1 allows and all it allows. */}
            {splits.length > 0 && (
              <tr>
                <td style={{ paddingLeft: 24 }}>
                  The May Firm <span style={s.tag}>the remainder</span>
                </td>
                <td style={{ ...s.r, color: firmShare < 0 ? '#b91c1c' : undefined }}>
                  {money(firmShare)}
                </td>
              </tr>
            )}
            <BreakRows rows={splits} setRows={setSplits}
                       placeholder="Jacoby & Myers"
                       addLabel="+ split the fee with another firm"
                       aria="Fee split" changeFor={rowChange} />
            <tr>
              <td>COSTS <span style={s.tag}>from Case Financials</span></td>
              <td style={s.r}><input style={s.num} value={form.costs} onChange={set('costs')} /><Was change={changedField.costs} /></td>
            </tr>
            <tr>
              <td><b>LIENS</b></td>
              <td style={s.r}><b>{money(liensTotal)}</b></td>
            </tr>
            {liens.map((l, i) => (
              <tr key={i}>
                <td style={{ paddingLeft: 24 }}>
                  <input style={{ ...s.input, width: '72%' }} value={l.name}
                         onChange={(e) => setLiens((xs) => xs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  {l.source && <span style={s.tag}>{srcLabel[l.source] || l.source}</span>}
                  {/* What this party originally claimed. It prints on the
                      client's letter in brackets, so it is editable here
                      rather than taken on trust from the Meds tab. Blank is
                      a normal state — plenty of lines have nothing on file. */}
                  <div style={s.origRow}>
                    <select style={s.origKind} value={l.original_kind || 'billed'}
                            aria-label={`How ${l.name || 'this line'} claimed its original figure`}
                            onChange={(e) => setLiens((xs) => xs.map((x, j) => j === i ? { ...x, original_kind: e.target.value } : x))}>
                      <option value="billed">billed</option>
                      <option value="asserted">originally asserted</option>
                    </select>
                    <input style={s.origNum} value={l.original ?? ''}
                           placeholder="nothing on file"
                           aria-label={`What ${l.name || 'this line'} originally claimed`}
                           onChange={(e) => setLiens((xs) => xs.map((x, j) => j === i ? { ...x, original: e.target.value } : x))} />
                  </div>
                </td>
                <td style={s.r}>
                  <input style={s.num} value={l.amount}
                         onChange={(e) => setLiens((xs) => xs.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} />
                  <Was change={rowChange(l.name)} />
                  {i > 0 && (
                    <button style={s.merge} title="Merge into the line above (adds the amounts)"
                            onClick={() => mergeUp(i)}>⌃</button>
                  )}
                  <button style={s.x} title="Remove"
                          onClick={() => setLiens((xs) => xs.filter((_, j) => j !== i))}>×</button>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={2} style={{ paddingLeft: 24 }}>
                <button style={s.ghost} onClick={() => setLiens((xs) => [...xs, { name: '', amount: '', original: '', original_kind: 'billed' }])}>
                  + add lien
                </button>
              </td>
            </tr>
            {/* Loans, client advances and child support come out of the
                client's own share rather than off the top, so they sit under
                a gross subtotal. With nothing to deduct the subtotal is the
                same figure as the net, and printing both only invites the
                question of which one is theirs — so the block appears, here
                and on the letter, only when there is something in it. */}
            {advances.length > 0 && (
              <tr>
                <td><b>CLIENT GROSS PROCEEDS</b></td>
                <td style={s.r}><b>{money(grossProceeds)}</b></td>
              </tr>
            )}
            {advances.length > 0 && (
              <tr>
                <td><b>LOANS &amp; ADVANCES</b></td>
                <td style={s.r}><b>{money(advancesTotal)}</b></td>
              </tr>
            )}
            <BreakRows rows={advances} setRows={setAdvances}
                       placeholder="Oasis Financial / child support / firm advance"
                       addLabel="+ add a loan, advance or child support"
                       aria="Loan or advance" changeFor={rowChange} />
            <tr>
              <td style={{ borderTop: '2px solid #171D29', paddingTop: 8 }}><b>CLIENT FINAL NET PROCEEDS</b></td>
              <td style={{ ...s.r, borderTop: '2px solid #171D29', paddingTop: 8,
                           color: net < 0 ? '#b91c1c' : '#171D29' }}><b>{money(net)}</b></td>
            </tr>
          </tbody>
        </table>
        <div style={s.hint}>
          Costs come from Case Financials, not Filevine — check the figure
          against the file before generating.
        </div>
        {sheetErrors.map((msg, i) => (
          <div key={i} style={s.error}>{msg}</div>
        ))}
        {net < 0 && (
          <div style={s.error}>
            Payments exceed the settlement by {money(Math.abs(net))}. The letter will not
            generate until the sheet balances.
          </div>
        )}
        {/* Named by the tab they are actually on, with the fix spelled out.
            This used to call every one of them a Meds tab provider with no
            negotiation. One of them was a lienholder on the LIENS tab that
            simply had no final amount typed in yet, so the preparer went
            looking for it on a tab it was never on. Catching it was right;
            pointing the wrong way was the bug. */}
        {!recon && skipped.length > 0 && (
          <div style={s.warn}>
            <b>{skipped.length === 1
              ? 'One lienholder is on this case but not on the letter'
              : `${skipped.length} lienholders are on this case but not on the letter`}</b>
            {' '}— there is no agreed amount in Lien Machine for them yet.
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {skipped.map((row, i) => (
                <li key={`${row.name}:${i}`} style={{ marginBottom: 3 }}>
                  <b>{row.name}</b> —{' '}
                  {row.where === 'liens'
                    ? 'on the Liens tab with no final amount. Open the Liens '
                      + 'tab on this case and enter the final lien amount, then '
                      + 'reopen this form.'
                    : 'on the Meds tab with a balance and no accepted '
                      + 'negotiation. Accept a negotiation for them, or confirm '
                      + 'there is no lien to pay.'}
                </li>
              ))}
            </ul>
            <div style={{ marginTop: 5, opacity: .85 }}>
              Only agreed figures are pulled — what the firm actually settled on.
              If one of these genuinely does not belong on the letter, leave it;
              if it does and you have the figure, you can add it by hand below.
            </div>
          </div>
        )}
        {ctx && liens.length === 0 && (
          <div style={s.warn}>
            No liens found on this case. Add them by hand, or check the Meds and
            Liens tabs — the letter will otherwise say the client keeps everything
            after fees and costs.
          </div>
        )}

        <div style={s.section}>Released parties</div>
        <div style={s.hint}>
          Pre-filled with the carriers on this case. <b>Defendants are not pulled from
          Filevine</b> — add them, and check the whole line against the signed release.
          This prints as “you have no further rights against …”.
        </div>
        <textarea style={{ ...s.textarea, minHeight: 46, marginTop: 6 }}
                  value={form.released_parties} onChange={set('released_parties')}
                  placeholder="Example Corp. or their insurance, Example Mutual Insurance" />

        <div style={partiesConfirmed ? s.okbox : s.warn}>
          <label style={s.checkRow}>
            <input
              type="checkbox"
              checked={partiesConfirmed}
              onChange={(e) => setPartiesConfirmed(e.target.checked)}
            />
            <span>
              I have checked these against the <b>signed release</b> and they
              are the parties to be released.
            </span>
          </label>
          <div style={{ marginTop: 6, fontSize: 11, opacity: .9 }}>
            The carriers above are pre-filled from the case, and defendants are
            not pulled at all. The signed release is the only authority for
            this line — it prints as “you have no further rights against …”.
          </div>
        </div>

        {missing.length > 0 && (
          <div style={s.warn}>Still needed: {missing.join(', ')}</div>
        )}

        {sent && (
          <div style={s.okbox}>
            <strong>Sent to closeout.</strong> {sent.client_name} is waiting on
            approval.
            {sent.can_approve
              ? ' You can sign it off yourself from the Closeout tab; doing so is recorded as a self-approval.'
              : ' An approver has to sign it off from the Closeout tab.'}
            {sent.slack_posted && ' Posted to the closeout channel.'}
            <div style={{ marginTop: 4 }}>
              Editing this sheet now voids that packet and it has to go back through.
            </div>
            {!sent.slack_posted && (
              <div style={{ ...s.warn, marginTop: 8 }}>
                <b>It was not posted to the closeout channel.</b>{' '}
                {sent.slack_error || 'Slack is not set up on the server yet.'}
                {' '}The packet is saved either way — tell the approver directly
                until this is sorted.
              </div>
            )}
          </div>
        )}

        {recon && (
          <div style={s.panel}>
            <div style={s.section}>
              Checked against Filevine
              {checkedAt && (
                <span style={{ fontWeight: 400, textTransform: 'none',
                               letterSpacing: 0, opacity: .75, marginLeft: 8 }}>
                  as of {checkedAt.toLocaleTimeString()}
                </span>
              )}
            </div>
            {recon.clean && (
              <div style={s.okbox}>
                Every lienholder and provider on this case is accounted for.
              </div>
            )}
            {/* Keyed by position as well as code. The tick-boxes below are
                identified by code and must stay that way, but React keys are a
                different job: two findings that legitimately share a code
                collapsed into one row, which hid a second open negotiation
                behind the first. */}
            {(recon.blocking || []).map((f, i) => (
              <div key={`${f.code}:${i}`} style={s.error}>
                <strong>Must fix.</strong> {f.message}
                {f.overridable === false && (
                  <div style={{ marginTop: 4 }}>
                    Nobody can override this one — there is no decision in it,
                    only something to correct above.
                  </div>
                )}
              </div>
            ))}
            {(recon.warnings || []).map((f, i) => (
              <label key={`${f.code}:${i}`} style={{ ...s.warn, ...s.checkRow }}>
                <input type="checkbox" checked={acks.includes(f.code)}
                       onChange={() => toggleAck(f.code)} style={{ marginTop: 2 }} />
                <span>{f.message}</span>
              </label>
            ))}
            {(recon.warnings || []).length > 0 && (
              <div style={{ marginTop: 8 }}>
                <label style={s.label}>Why these are fine (goes to the approver)</label>
                <textarea style={{ ...s.textarea, minHeight: 52 }} value={ackNote}
                          onChange={(e) => setAckNote(e.target.value)}
                          placeholder="e.g. client settled with Valley Ortho directly — no lien" />
              </div>
            )}
            {(recon.info || []).length > 0 && (
              <div style={{ marginTop: 12 }}>
                <label style={s.label}>For reference — nothing to confirm</label>
                {recon.info.map((f, i) => (
                  <div key={`${f.code}:${i}`} style={s.refline}>{f.message}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Who this goes to. Sits immediately above the send button because
            it is the last decision before the packet leaves — and because the
            default is right most days, which is exactly when a field further
            up the sheet stops being read. */}
        {(ctx?.approvers || []).length > 0 && (
          <div style={s.panel}>
            <div style={s.section}>Approval</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              {/* A litigation closeout is read by one of the firm's lawyers
                  before it reaches closeout at all. First stop, so it sits
                  first. */}
              {isLit && (
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <label style={s.label} htmlFor="lm-attorney">Attorney review</label>
                  <select id="lm-attorney" style={s.input} value={attorney}
                          onChange={(e) => setAttorney(e.target.value)}>
                    <option value="">Choose a reviewing attorney…</option>
                    {(ctx?.attorneys || []).map((a) => (
                      <option key={a.email} value={a.email}>
                        {a.name}{a.can_be_tagged ? '' : ' — no Slack ID on file'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                <label style={s.label} htmlFor="lm-approver">{isLit ? 'Then to closeout' : 'Send to'}</label>
                <select id="lm-approver" style={s.input} value={approver}
                        onChange={(e) => setApprover(e.target.value)}>
                  <option value="">The closeout channel — nobody tagged</option>
                  {(ctx.approvers || []).map((a) => (
                    <option key={a.email} value={a.email}>
                      {a.name}{a.can_be_tagged ? '' : ' — no Slack ID on file'}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {isLit && (ctx?.attorneys || []).length === 0 && (
              <div style={s.warn}>
                No reviewing attorneys are configured, so this litigation
                packet has nowhere to go. SETTLEMENT_ATTORNEYS needs setting
                before a lit closeout can be sent.
              </div>
            )}
            {isLit && (
              <div style={s.hint}>
                It goes to the attorney first. If they change anything it comes
                back to you with their reason before it reaches closeout.
              </div>
            )}
            <div style={s.hint}>
              {approver
                ? 'They are tagged in the closeout post so it lands in their '
                  + 'mentions. Any approver can still act on it — this chooses '
                  + 'who is asked, not who is allowed.'
                : 'The packet still posts to the closeout channel, but nobody '
                  + 'is notified. Pick a name if someone should be.'}
            </div>
            {(() => {
              const picked = (ctx.approvers || []).find((a) => a.email === approver);
              if (!approver || !picked || picked.can_be_tagged) return null;
              // Two different fixes, so two different sentences. A missing ID
              // needs someone to go and find one; a rejected one is already
              // set and wrong, which reads identically in the channel.
              return (
                <div style={s.warn}>
                  {picked.id_problem
                    ? <>The Slack ID set for {picked.name} (<code>{picked.id_problem}</code>)
                        is not a member ID — those start with U. A <code>D…</code> is
                        a direct-message channel, which is what Slack copies from
                        a DM link.</>
                    : <>{picked.name} has no Slack member ID configured.</>}
                  {' '}They will be named on the closeout post but not notified.
                </div>
              );
            })()}
          </div>
        )}

        <div style={s.actions}>
          {savedAt && (
            <span style={{ ...s.hint, alignSelf: 'center', marginRight: 'auto' }}>
              Saved {savedAt.toLocaleTimeString()}
            </span>
          )}
          {/* "Save draft" sat next to "Download draft", where draft meant a
              document. A preparer read this one the same way and went
              looking for the saved letter it had not made — it saves what
              she has typed on this sheet, nothing else. Named for that. */}
          <button style={s.cancel} onClick={saveDraft} disabled={saving || busy}
                  title="Saves what you have typed on this sheet so it is here when you come back. It does not make a letter.">
            {saving ? 'Saving…' : 'Save my work'}
          </button>
          <button style={s.cancel} onClick={close} disabled={busy}>Close</button>
          <button style={{ ...s.cancel, opacity: canGenerate ? 1 : 0.5,
                           cursor: canGenerate ? 'pointer' : 'not-allowed' }}
                  onClick={generate} disabled={!canGenerate}>
            {busy ? 'Working…' : 'Download draft'}
          </button>
          {/* The approved copy, reachable from the case rather than only
              from the Closeout queue. Held back until the preparer has read
              what the approver altered, when they altered anything. */}
          {approved && !sent && !sentBack && (
            <button style={{ ...s.primary, opacity: finalReady ? 1 : 0.5,
                             cursor: finalReady ? 'pointer' : 'not-allowed' }}
                    onClick={downloadFinal} disabled={!finalReady}
                    title={finalReady ? 'The approved letter, without the DRAFT stamp'
                                      : 'Tick that you have read the approver’s changes first'}>
              {busy ? 'Working…' : 'Download the final letter'}
            </button>
          )}
          {/* Once the checks have run the primary button becomes Send, and
              without this there is no way back to them: a finding fixed on
              the case — a negotiation accepted, a lien entered — leaves the
              sheet showing the old result with nothing to press. Editing the
              sheet clears the findings, but the fix is usually not on the
              sheet. */}
          {recon && !sent && (
            <button style={s.cancel} onClick={runChecks} disabled={checking || busy}>
              {checking ? 'Checking…' : 'Re-check'}
            </button>
          )}
          {/* Signed off with changes: accepting IS the approval, so this
              hands over the letter rather than sending anything up. */}
          {signedOff && (
            <button style={{ ...s.primary, opacity: acceptReady ? 1 : 0.5,
                             cursor: acceptReady ? 'pointer' : 'not-allowed' }}
                    onClick={acceptAndFinish} disabled={!acceptReady}
                    title={acceptReady
                      ? 'Accepts their figures and downloads the approved letter'
                      : 'Tick that you have read what they changed first'}>
              {busy ? 'Working…' : 'Accept & download the final letter'}
            </button>
          )}
          {accepted && (
            <span style={{ ...s.hint, alignSelf: 'center' }}>
              Accepted — the letter has been released and downloaded.
            </span>
          )}
          {/* Their edits, taken as they stand. Only offered on a packet that
              came back WITH changes — on one sent back empty-handed there is
              nothing to accept, and the preparer has edits of their own to
              make before anything goes up again. */}
          {sentBack && !sent && !recon && !signedOff
            && (sentBack.changes || []).length > 0 && (
            <button style={{ ...s.cancel, opacity: canGenerate ? 1 : 0.5,
                             cursor: canGenerate ? 'pointer' : 'not-allowed' }}
                    onClick={acceptAndResubmit} disabled={!canGenerate || checking || busy}
                    title={canGenerate
                      ? 'Runs the checks on their version and sends it straight back up if it is clean'
                      : 'Confirm the released parties first'}>
              {checking || busy ? 'Working…' : 'Accept their changes & send back'}
            </button>
          )}
          {sent ? (
            <button style={s.primary} onClick={close}>Done</button>
          ) : !recon ? (
            <button style={{ ...s.primary, opacity: canGenerate ? 1 : 0.5,
                             cursor: canGenerate ? 'pointer' : 'not-allowed' }}
                    onClick={runChecks} disabled={!canGenerate || checking}>
              {checking ? 'Checking…' : 'Check & send to closeout'}
            </button>
          ) : escalatable ? (
            <button style={s.primary} onClick={() => submitForApproval(true)}
                    disabled={busy}>
              {busy ? 'Sending…' : 'Send up for a management decision'}
            </button>
          ) : (
            <button style={{ ...s.primary, opacity: canSubmit && !pending ? 1 : 0.5,
                             cursor: canSubmit && !pending ? 'pointer' : 'not-allowed' }}
                    onClick={() => submitForApproval(false)}
                    disabled={!canSubmit || !!pending}>
              {busy ? 'Sending…' : 'Send to closeout'}
            </button>
          )}
        </div>
        <div style={{ ...s.hint, textAlign: 'right' }}>
          {sent
            ? 'The approver reads the stored letter — not a fresh one built from this form.'
            : pending
            ? 'Withdraw the packet in Closeout before sending this case again.'
            : escalatable
            ? 'An approver can clear these with a reason on the record. You cannot — this sends it to them to decide.'
            : !partiesConfirmed && missing.length === 0 && net >= 0
            ? 'Confirm the released parties first.'
            : recon && (recon.blocking || []).length > 0
            ? `Fix the ${(recon.blocking || []).length} item${(recon.blocking || []).length === 1 ? '' : 's'} in red on the case, then Re-check — a tick-box and a note won't clear ${(recon.blocking || []).length === 1 ? 'it' : 'them'}.`
            : recon && unackedCount > 0
            ? `Confirm ${unackedCount} item${unackedCount === 1 ? '' : 's'} above to send.`
            : 'Downloads are stamped DRAFT. The clean letter is released once closeout approves it.'}
        </div>
      </div>
    </div>
  );
}

/**
 * A stored packet's payload back into the shape this form edits.
 *
 * The payload is letter-builder kwargs — address as a list of lines, liens as
 * [name, amount, original, kind] rows — so it needs turning back into the
 * flat form state and the separate liens list.
 */
function formFromPayload(p) {
  return {
    client_name: p.client_name ?? '',
    client_last_name: p.client_last_name ?? '',
    client_title: p.client_title ?? '',
    client_address: (p.client_address_lines || []).join('\n'),
    client_cell: p.client_cell ?? '',
    client_email: p.client_email ?? '',
    matter_description: p.matter_description ?? '',
    date_of_loss: p.date_of_loss ?? '',
    settlement_amount: p.settlement_amount ?? '',
    attorney_fees: trimToCents(p.attorney_fees),
    costs: p.costs ?? '',
    released_parties: p.released_parties ?? '',
  };
}

// The three breakdowns round-trip as [name, amount] pairs, in and out.
// Kept as strings in the form so a half-typed figure is not read as a number
// mid-keystroke, exactly as the lien rows are.
/**
 * The figure as it was before the approver moved it, struck through under
 * the box that now holds their number.
 */
function Was({ change }) {
  if (!change) return null;
  const gone = change.now == null;
  return (
    <div style={s.wasLine}>
      {gone ? 'taken off by the approver' : <>was <s>{change.was_text}</s></>}
    </div>
  );
}

function rowsFromPayload(rows) {
  return (rows || []).map((r) => ({ name: r[0] ?? '', amount: String(r[1] ?? '') }));
}

function rowsOut(rows, n) {
  return (rows || [])
    .filter((r) => (r.name || '').trim() && n(r.amount) !== 0)
    .map((r) => [r.name.trim(), n(r.amount)]);
}

function liensFromPayload(p) {
  return (p.liens || []).map((row) => ({
    name: row[0],
    amount: String(row[1] ?? ''),
    original: row[2] == null ? '' : String(row[2]),
    original_kind: row[3] || 'billed',
  }));
}


/**
 * One of the three breakdown blocks under a distribution line — settlement
 * sources, fee splits, loans and advances.
 *
 * All three are the same shape: named rows that either add up to the figure
 * above them or come out of it. Written once because three near-identical
 * copies is how one of them quietly stops matching the other two.
 */
function BreakRows({ rows, setRows, placeholder, addLabel, aria, changeFor }) {
  const edit = (i, k) => (e) =>
    setRows((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  return (
    <>
      {rows.map((r, i) => (
        <tr key={i}>
          <td style={{ paddingLeft: 24 }}>
            <input style={{ ...s.input, width: '72%' }} value={r.name}
                   placeholder={placeholder}
                   aria-label={`${aria} ${i + 1}, description`}
                   onChange={edit(i, 'name')} />
          </td>
          <td style={s.r}>
            <input style={s.num} value={r.amount}
                   aria-label={`${aria} ${i + 1}, amount`}
                   onChange={edit(i, 'amount')} />
            <button style={s.x} title="Remove"
                    onClick={() => setRows((xs) => xs.filter((_, j) => j !== i))}>×</button>
            {changeFor && <Was change={changeFor(r.name)} />}
          </td>
        </tr>
      ))}
      <tr>
        <td colSpan={2} style={{ paddingLeft: 24 }}>
          <button style={s.ghost}
                  onClick={() => setRows((xs) => [...xs, { name: '', amount: '' }])}>
            {addLabel}
          </button>
        </td>
      </tr>
    </>
  );
}

function F({ label, v, on, req, placeholder }) {
  return (
    <div style={{ flex: 1, minWidth: 0, marginBottom: 10 }}>
      <label style={s.label}>{label}{req && <span style={{ color: '#dc2626' }}> *</span>}</label>
      <input style={s.input} value={v ?? ''} onChange={on} placeholder={placeholder} />
    </div>
  );
}

const s = {
  backdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 1100, padding: 20 },
  modal: { background: 'white', borderRadius: 12, padding: '24px 28px',
    width: 'min(760px, 96vw)', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 12px 32px rgba(0,0,0,0.2)' },
  title: { fontFamily: "'Arial Black', Impact, sans-serif", fontSize: 18,
    color: '#171D29', marginBottom: 4 },
  subtitle: { fontSize: 13, color: '#6b7585', lineHeight: 1.5, marginBottom: 8 },
  loading: { fontSize: 12, color: '#6b7585', marginBottom: 10 },
  error: { background: '#fee2e2', color: '#991b1b', padding: '8px 12px',
    borderRadius: 6, marginTop: 8, marginBottom: 8, fontSize: 12, lineHeight: 1.45 },
  ul: { margin: '6px 0 0 0', paddingLeft: 18 },
  origRow: { display: 'flex', gap: 5, alignItems: 'center', marginTop: 4 },
  origKind: { fontSize: 10.5, color: '#6b7585', border: '1px solid #e5e7eb',
    borderRadius: 4, padding: '2px 4px', fontFamily: 'inherit', background: '#fafafa' },
  origNum: { width: 110, fontSize: 11.5, padding: '3px 6px', border: '1px solid #e5e7eb',
    borderRadius: 4, fontFamily: 'inherit', color: '#171D29', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums' },
  warn: { background: '#fef3c7', color: '#92400e', padding: '8px 12px',
    borderRadius: 6, marginTop: 8, marginBottom: 8, fontSize: 12, lineHeight: 1.45 },
  section: { fontFamily: "'Arial Black', Impact, sans-serif", fontSize: 11,
    color: '#A1B808', letterSpacing: 1, textTransform: 'uppercase',
    margin: '16px 0 8px 0', borderTop: '1px solid #f3f4f6', paddingTop: 12 },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  label: { display: 'block', fontSize: 10, color: '#6b7585', textTransform: 'uppercase',
    letterSpacing: 0.5, fontWeight: 700, marginBottom: 4 },
  input: { width: '100%', boxSizing: 'border-box', padding: '7px 9px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#171D29',
    fontFamily: 'inherit' },
  num: { width: 118, textAlign: 'right', padding: '5px 8px', border: '1px solid #d1d5db',
    borderRadius: 5, fontSize: 13, fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums' },
  textarea: { width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#171D29',
    fontFamily: 'inherit', resize: 'vertical' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 4 },
  r: { textAlign: 'right', whiteSpace: 'nowrap' },
  // The same red strike the approver sees on a net they have moved, so the
  // two sides of the handover mark a changed figure the same way.
  wasLine: { fontSize: 10.5, color: '#b91c1c', marginTop: 2, whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums' },
  tag: { fontSize: 10, color: '#6b7585', background: '#edf2f7', padding: '1px 6px',
    borderRadius: 9, marginLeft: 6 },
  merge: { marginLeft: 6, border: 'none', background: 'none', color: '#6b7585',
    cursor: 'pointer', fontSize: 14, lineHeight: 1, fontWeight: 700 },
  x: { marginLeft: 6, border: 'none', background: 'none', color: '#b91c1c',
    cursor: 'pointer', fontSize: 15, lineHeight: 1 },
  tplOn: { border: '1px solid #A1B808', background: '#f4f8e1', color: '#171D29',
    borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit' },
  tplOff: { border: '1px solid #d1d5db', background: 'white', color: '#6b7585',
    borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer',
    fontFamily: 'inherit' },
  ghost: { border: '1px dashed #d1d5db', background: 'none', color: '#6b7585',
    borderRadius: 5, padding: '3px 10px', fontSize: 12, cursor: 'pointer',
    fontFamily: 'inherit' },
  hint: { fontSize: 11, color: '#9ca3af', marginTop: 3, lineHeight: 1.4 },
  okbox: { background: '#ecfdf5', color: '#065f46', padding: '8px 12px',
    borderRadius: 6, marginTop: 8, marginBottom: 8, fontSize: 12, lineHeight: 1.45 },
  checkRow: { display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' },
  panel: { border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 12px 12px',
    marginTop: 12, background: '#fafbfc' },
  refline: { fontSize: 12, color: '#4b5563', background: '#f3f4f6',
    padding: '6px 12px', borderRadius: 6, marginTop: 8, lineHeight: 1.45 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  cancel: { padding: '8px 14px', border: '1px solid #d1d5db', background: 'white',
    color: '#171D29', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  primary: { padding: '8px 16px', border: '1px solid #A1B808', background: '#A1B808',
    color: '#171D29', borderRadius: 6, fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
};

export default SettlementLetterModal;
